/**
 * Patch-management API service — docs/patch-management/03-interface-contract.md §9.
 * (The runner callback endpoint is intentionally not exposed here.)
 *
 * Error handling: the shared axios client redirects the whole page to /login on
 * any 401 *or 403*, which would swallow the portal's per-action 403 messages
 * (e.g. "not an approver for uat"). Every call here therefore asks axios to
 * resolve on everything except 401 and converts HTTP errors into a
 * `PatchApiError` that carries the server's message verbatim. 401 still rejects,
 * so the existing session-expiry redirect keeps working.
 */
import type { AxiosResponse } from 'axios'
import { api } from './api'
import { isPatchEnvironment, isValidCampaignId, type FieldErrors } from '@/utils/patchValidation'
import {
  PATCH_ENVIRONMENTS,
  type AdvisorResult,
  type ApproveStageBody,
  type ApproverAssignment,
  type CreatePatchCampaignRequest,
  type AzureSubscriptionConfig,
  type CampaignReport,
  type DeletedSubscription,
  type ExecuteStageBody,
  type PatchAuditEvent,
  type PatchCampaign,
  type PatchEnvironment,
  type PatchInventoryResource,
  type PatchMe,
  type PatchRecommendationRow,
  type PatchScheduleRow,
  type PatchScope,
  type PatchStage,
  type PostPatchCheck,
  type PrePatchCheck,
  type RejectStageBody,
  type RunnerStageStep,
  type ScheduleStageBody,
  type TagMode,
  type TagResolution,
  type TagWriteResult,
  type VmTag,
} from '@/types/patching'

// ─── Errors ───────────────────────────────────────────────────────────────────

export class PatchApiError extends Error {
  readonly status: number | null
  readonly fieldErrors: FieldErrors

  constructor(message: string, status: number | null, fieldErrors: FieldErrors = {}) {
    super(message)
    this.name = 'PatchApiError'
    this.status = status
    this.fieldErrors = fieldErrors
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function collectMessages(value: unknown, path: string, out: FieldErrors): void {
  if (typeof value === 'string') {
    const list = out[path] ?? []
    list.push(value)
    out[path] = list
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (typeof item === 'string') collectMessages(item, path, out)
      else collectMessages(item, path ? `${path}.${index}` : String(index), out)
    })
  } else if (isRecord(value)) {
    for (const [key, child] of Object.entries(value)) {
      collectMessages(child, path ? `${path}.${key}` : key, out)
    }
  }
}

/** Pull a human-readable message (and DRF field errors) out of an error response body. */
export function extractApiError(data: unknown, status: number | null): { message: string; fieldErrors: FieldErrors } {
  const fallback = status ? `Request failed with status ${status}.` : 'Request failed.'
  if (typeof data === 'string') {
    const text = data.trim()
    // Never dump an HTML error page into the UI.
    return { message: text && text.length <= 300 && !text.startsWith('<') ? text : fallback, fieldErrors: {} }
  }
  if (Array.isArray(data)) {
    const fieldErrors: FieldErrors = {}
    collectMessages(data, '', fieldErrors)
    const message = Object.values(fieldErrors).flat().join(' ')
    return { message: message || fallback, fieldErrors: {} }
  }
  if (!isRecord(data)) return { message: fallback, fieldErrors: {} }

  const { detail, message: rawMessage, ...rest } = data
  if (typeof detail === 'string' && detail.trim()) return { message: detail, fieldErrors: {} }
  if (typeof rawMessage === 'string' && rawMessage.trim()) return { message: rawMessage, fieldErrors: {} }

  const fieldErrors: FieldErrors = {}
  collectMessages(detail, '', fieldErrors)
  collectMessages(rest, '', fieldErrors)
  const generic = fieldErrors['non_field_errors'] ?? fieldErrors[''] ?? []
  delete fieldErrors['non_field_errors']
  delete fieldErrors['']
  const parts = [
    ...generic,
    ...Object.entries(fieldErrors).map(([path, messages]) => `${path}: ${messages.join(' ')}`),
  ]
  return { message: parts.join(' ') || fallback, fieldErrors }
}

/** Convert anything thrown by the axios client into a PatchApiError. */
export function toPatchApiError(err: unknown): PatchApiError {
  if (err instanceof PatchApiError) return err
  if (isRecord(err) && isRecord(err.response)) {
    const status = typeof err.response.status === 'number' ? err.response.status : null
    const { message, fieldErrors } = extractApiError(err.response.data, status)
    return new PatchApiError(message, status, fieldErrors)
  }
  if (err instanceof Error && err.message) return new PatchApiError(err.message, null)
  return new PatchApiError('Request failed.', null)
}

// ─── Transport ────────────────────────────────────────────────────────────────

/** Resolve (don't reject) on everything but 401 so the global 403→/login redirect never fires. */
const REQUEST_CONFIG = { validateStatus: (status: number) => status !== 401 } as const

/** Calls that fan out to Azure get a minute instead of the client's default 20 seconds. */
const SLOW_REQUEST_CONFIG = { ...REQUEST_CONFIG, timeout: 60_000 } as const

async function send<T>(call: () => Promise<AxiosResponse<T>>): Promise<T> {
  let response: AxiosResponse<T>
  try {
    response = await call()
  } catch (err) {
    throw toPatchApiError(err)
  }
  if (response.status >= 400) {
    const { message, fieldErrors } = extractApiError(response.data, response.status)
    throw new PatchApiError(message, response.status, fieldErrors)
  }
  return response.data
}

// ─── Paths (validated — ids and environments come from the URL / user input) ─

const BASE = '/patching'

function campaignPath(campaignId: string): string {
  if (!isValidCampaignId(campaignId)) {
    throw new PatchApiError(`"${String(campaignId)}" is not a valid campaign id.`, null)
  }
  return `${BASE}/campaigns/${campaignId}`
}

function tagId(id: number): number {
  if (!Number.isInteger(id) || id <= 0) throw new PatchApiError(`"${String(id)}" is not a valid id.`, null)
  return id
}

function stagePath(campaignId: string, env: PatchEnvironment, action: string): string {
  if (!isPatchEnvironment(env)) {
    throw new PatchApiError(`"${String(env)}" is not a valid environment.`, null)
  }
  return `${campaignPath(campaignId)}/stages/${env}/${action}/`
}

// ─── Response normalisation (tolerate partially populated payloads) ──────────

type RawStage = Partial<PatchStage> & Pick<PatchStage, 'environment' | 'status'>
type RawCampaign = Partial<Omit<PatchCampaign, 'stages'>> & { id: string; stages?: RawStage[] }

function normalizeProgress(value: unknown): RunnerStageStep['progress'] {
  if (!value || typeof value !== 'object') return undefined
  const row = value as Record<string, unknown>
  const count = (key: string) => (typeof row[key] === 'number' && row[key] >= 0 ? Math.floor(row[key] as number) : 0)
  const phase = typeof row.phase === 'string' ? row.phase : ''
  if (!phase) return undefined
  return {
    installed: count('installed'),
    pending: count('pending'),
    failed: count('failed'),
    not_selected: count('not_selected'),
    phase,
    started_at: typeof row.started_at === 'string' ? row.started_at : null,
  }
}

function normalizePostChecks(value: unknown): PostPatchCheck[] {
  if (!Array.isArray(value)) return []
  const allowed = new Set(['passed', 'failed', 'running', 'not_checked'])
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return []
    const row = item as Record<string, unknown>
    const status = typeof row.status === 'string' ? row.status : 'not_checked'
    if (!allowed.has(status)) return []
    return [{
      check: typeof row.check === 'string' ? row.check : 'check',
      status,
      mandatory: row.mandatory === true,
      detail: typeof row.detail === 'string' ? row.detail : '',
    }]
  })
}

function normalizeRunnerStages(value: unknown): Record<string, RunnerStageStep> {
  if (!value || typeof value !== 'object') return {}
  const steps: Record<string, RunnerStageStep> = {}
  for (const [name, entry] of Object.entries(value)) {
    if (!entry || typeof entry !== 'object') continue
    const row = entry as Record<string, unknown>
    const progress = normalizeProgress(row.progress)
    steps[name] = {
      status: typeof row.status === 'string' ? row.status : '',
      message: typeof row.message === 'string' ? row.message : '',
      at: typeof row.at === 'string' ? row.at : null,
      ...(progress ? { progress } : {}),
    }
  }
  return steps
}

function normalizeStage(raw: RawStage): PatchStage {
  return {
    id: raw.id ?? 0,
    environment: raw.environment,
    status: raw.status,
    run_id: raw.run_id ?? null,
    run_url: raw.run_url ?? null,
    targets: raw.targets,
    plan: raw.plan ?? null,
    approvals: raw.approvals ?? [],
    assessments: raw.assessments ?? [],
    assessment_limitations: raw.assessment_limitations ?? [],
    risks: raw.risks ?? [],
    rollout: raw.rollout ?? null,
    results: raw.results ?? [],
    runner_stages: normalizeRunnerStages(raw.runner_stages),
    post_validation: normalizePostChecks(raw.post_validation),
    acceptance: raw.acceptance ?? null,
    failure: raw.failure ?? null,
    dry_run: raw.dry_run,
    skipped_targets: raw.skipped_targets ?? [],
    dry_run_result: raw.dry_run_result ?? null,
    maintenance_window: raw.maintenance_window ?? null,
    stale: raw.stale === true,
    last_callback_at: raw.last_callback_at ?? null,
    dispatch_unconfirmed: raw.dispatch_unconfirmed === true,
    dispatch_attempt: raw.dispatch_attempt,
    run_cancel: raw.run_cancel ?? null,
    started_at: raw.started_at ?? null,
    finished_at: raw.finished_at ?? null,
    updated_at: raw.updated_at ?? null,
  }
}

function emptyScope(product: string): PatchScope {
  return {
    schema_version: 1,
    product,
    classifications: { windows: [], linux: [] },
    kb_numbers_to_include: [],
    kb_numbers_to_exclude: [],
    package_name_masks_to_include: [],
    package_name_masks_to_exclude: [],
    max_patch_publish_date: null,
    reboot_setting: 'IfRequired',
    applications: [],
  }
}

export function normalizeCampaign(raw: RawCampaign): PatchCampaign {
  const product = raw.product ?? raw.patch_scope?.product ?? ''
  const stages = (raw.stages ?? [])
    .filter((s) => isPatchEnvironment(s.environment))
    .map(normalizeStage)
    .sort((a, b) => PATCH_ENVIRONMENTS.indexOf(a.environment) - PATCH_ENVIRONMENTS.indexOf(b.environment))
  return {
    id: raw.id,
    name: raw.name ?? '',
    description: raw.description ?? '',
    product,
    change_ticket: raw.change_ticket ?? '',
    requested_by: raw.requested_by ?? '',
    requested_by_name: raw.requested_by_name ?? null,
    patch_scope: raw.patch_scope ?? emptyScope(product),
    scope_hash: raw.scope_hash ?? '',
    stages,
    counts: raw.counts,
    created_at: raw.created_at ?? '',
    updated_at: raw.updated_at ?? null,
  }
}

/** DRF may return a bare array or `{ count, next, previous, results }`. */
function unwrapList<T>(data: T[] | { results?: T[] } | null | undefined): T[] {
  if (Array.isArray(data)) return data
  return data?.results ?? []
}

function toText(item: unknown): string {
  if (typeof item === 'string') return item
  if (isRecord(item)) {
    for (const key of ['text', 'title', 'summary', 'description', 'message']) {
      const value = item[key]
      if (typeof value === 'string' && value) return value
    }
  }
  return JSON.stringify(item)
}

function normalizeAdvisor(raw: Partial<AdvisorResult> | null | undefined): AdvisorResult {
  const data = raw ?? {}
  return {
    available: data.available === true,
    summary: typeof data.summary === 'string' ? data.summary : '',
    priorities: (Array.isArray(data.priorities) ? data.priorities : []).map(toText),
    missing_prerequisites: (Array.isArray(data.missing_prerequisites) ? data.missing_prerequisites : []).map(toText),
    invocation_id: data.invocation_id ?? null,
  }
}

export interface PatchCampaignList {
  campaigns: PatchCampaign[]
  /** Total on the server (equals campaigns.length for an unpaginated response). */
  count: number
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const patchingApi = {
  /** UI gating only — the server enforces every rule. */
  getMe: async (): Promise<PatchMe> => {
    const raw = await send(() => api.get<Partial<PatchMe>>(`${BASE}/me/`, REQUEST_CONFIG))
    return {
      email: raw.email ?? '',
      can_request: raw.can_request === true,
      approver_for: (raw.approver_for ?? []).filter(isPatchEnvironment),
      is_patch_admin: raw.is_patch_admin === true,
    }
  },

  listCampaigns: async (): Promise<PatchCampaignList> => {
    const raw = await send(() =>
      api.get<RawCampaign[] | { count?: number; results?: RawCampaign[] }>(`${BASE}/campaigns/`, REQUEST_CONFIG),
    )
    const campaigns = unwrapList(raw).map(normalizeCampaign)
    const count = !Array.isArray(raw) && typeof raw?.count === 'number' ? raw.count : campaigns.length
    return { campaigns, count }
  },

  createCampaign: async (body: CreatePatchCampaignRequest): Promise<PatchCampaign> =>
    normalizeCampaign(await send(() => api.post<RawCampaign>(`${BASE}/campaigns/`, body, REQUEST_CONFIG))),

  getCampaign: async (campaignId: string): Promise<PatchCampaign> =>
    normalizeCampaign(await send(() => api.get<RawCampaign>(`${campaignPath(campaignId)}/`, REQUEST_CONFIG))),

  planStage: async (campaignId: string, env: PatchEnvironment): Promise<void> => {
    await send(() => api.post(stagePath(campaignId, env, 'plan'), undefined, REQUEST_CONFIG))
  },

  approveStage: async (campaignId: string, env: PatchEnvironment, body: ApproveStageBody): Promise<void> => {
    await send(() => api.post(stagePath(campaignId, env, 'approve'), body, REQUEST_CONFIG))
  },

  rejectStage: async (campaignId: string, env: PatchEnvironment, body: RejectStageBody): Promise<void> => {
    await send(() => api.post(stagePath(campaignId, env, 'reject'), body, REQUEST_CONFIG))
  },

  executeStage: async (campaignId: string, env: PatchEnvironment, body: ExecuteStageBody): Promise<void> => {
    await send(() => api.post(stagePath(campaignId, env, 'execute'), body, REQUEST_CONFIG))
  },

  cancelStage: async (campaignId: string, env: PatchEnvironment): Promise<void> => {
    await send(() => api.post(stagePath(campaignId, env, 'cancel'), undefined, REQUEST_CONFIG))
  },

  getAudit: async (campaignId: string): Promise<PatchAuditEvent[]> => {
    const raw = await send(() =>
      api.get<PatchAuditEvent[] | { results?: PatchAuditEvent[] }>(`${campaignPath(campaignId)}/audit/`, REQUEST_CONFIG),
    )
    return unwrapList(raw)
  },

  /** Read-only AI advice. Never affects approvals or gates. */
  runAdvisor: async (campaignId: string): Promise<AdvisorResult> =>
    normalizeAdvisor(await send(() => api.post<Partial<AdvisorResult>>(`${campaignPath(campaignId)}/advisor/`, undefined, REQUEST_CONFIG))),

  listApproverAssignments: async (): Promise<ApproverAssignment[]> => {
    const raw = await send(() =>
      api.get<ApproverAssignment[] | { results?: ApproverAssignment[] }>(`${BASE}/approver-assignments/`, REQUEST_CONFIG),
    )
    return unwrapList(raw)
  },

  createApproverAssignment: (body: { environment: PatchEnvironment; user_email: string }): Promise<ApproverAssignment> =>
    send(() => api.post<ApproverAssignment>(`${BASE}/approver-assignments/`, body, REQUEST_CONFIG)),

  deleteApproverAssignment: async (id: number): Promise<void> => {
    if (!Number.isInteger(id) || id <= 0) throw new PatchApiError(`"${String(id)}" is not a valid assignment id.`, null)
    await send(() => api.delete(`${BASE}/approver-assignments/${id}/`, REQUEST_CONFIG))
  },

  // ── VM tags ────────────────────────────────────────────────────────────────

  listTags: async (connectionId?: number): Promise<VmTag[]> => {
    const query = connectionId === undefined ? '' : `?connection=${encodeURIComponent(String(connectionId))}`
    const raw = await send(() => api.get<{ results?: VmTag[] }>(`${BASE}/tags/${query}`, REQUEST_CONFIG))
    return raw.results ?? []
  },

  /** Create a tag, add VMs to one, or replace a tag's VMs, from the discovery table's selection. */
  saveTag: (
    mode: TagMode,
    body: { connection_id: number; name: string; description: string; resource_ids: number[]; tag_id?: number },
  ): Promise<TagWriteResult> => {
    if (mode === 'create') {
      return send(() =>
        api.post<TagWriteResult>(
          `${BASE}/tags/`,
          { connection_id: body.connection_id, name: body.name, description: body.description, resource_ids: body.resource_ids },
          REQUEST_CONFIG,
        ),
      )
    }
    if (!body.tag_id || !Number.isInteger(body.tag_id)) throw new PatchApiError('Choose the tag to change.', null)
    return send(() =>
      api.post<TagWriteResult>(
        `${BASE}/tags/${body.tag_id}/members/`,
        { resource_ids: body.resource_ids, replace: mode === 'replace', description: body.description || undefined },
        REQUEST_CONFIG,
      ),
    )
  },

  updateTag: (id: number, body: { name?: string; description?: string }): Promise<TagWriteResult> =>
    send(() => api.patch<TagWriteResult>(`${BASE}/tags/${tagId(id)}/`, body, REQUEST_CONFIG)),

  removeTagMember: (id: number, memberId: number): Promise<TagWriteResult> =>
    send(() => api.delete<TagWriteResult>(`${BASE}/tags/${tagId(id)}/members/${tagId(memberId)}/`, REQUEST_CONFIG)),

  deleteTag: (id: number) => send(() => api.delete<{ deleted: boolean; name: string; vms_untagged: number }>(`${BASE}/tags/${tagId(id)}/`, REQUEST_CONFIG)),

  resolveTag: (id: number): Promise<TagResolution> => send(() => api.get<TagResolution>(`${BASE}/tags/${tagId(id)}/resolve/`, REQUEST_CONFIG)),

  getPatchHealth: () => send(() => api.get<{ status: string; dispatch_backend: string; azure_live_execution: boolean }>(`${BASE}/health/`, REQUEST_CONFIG)),

  listSubscriptions: async (): Promise<AzureSubscriptionConfig[]> => {
    const raw = await send(() => api.get<{ results?: AzureSubscriptionConfig[] }>(`${BASE}/subscriptions/`, REQUEST_CONFIG))
    return raw.results ?? []
  },

  saveSubscription: (body: Record<string, unknown>) =>
    send(() => api.post<AzureSubscriptionConfig>(`${BASE}/subscriptions/`, body, REQUEST_CONFIG)),

  /** Super Admin only (the server enforces it). Removes the connection and the machines stored for it. */
  deleteSubscription: async (id: number): Promise<DeletedSubscription> => {
    if (!Number.isInteger(id) || id <= 0) throw new PatchApiError(`"${String(id)}" is not a valid connection id.`, null)
    return send(() => api.delete<DeletedSubscription>(`${BASE}/subscriptions/${id}/`, REQUEST_CONFIG))
  },

  validateSubscription: (id: number) =>
    send(() => api.post<AzureSubscriptionConfig>(`${BASE}/subscriptions/${id}/validate/`, undefined, REQUEST_CONFIG)),

  discoverSubscription: async (id: number): Promise<PatchInventoryResource[]> => {
    const raw = await send(() => api.post<{ results?: PatchInventoryResource[] }>(`${BASE}/subscriptions/${id}/discover/`, undefined, REQUEST_CONFIG))
    return raw.results ?? []
  },

  listResources: async (query: string): Promise<{ count: number; results: PatchInventoryResource[] }> =>
    send(() => api.get<{ count: number; results: PatchInventoryResource[] }>(`${BASE}/resources/${query}`, REQUEST_CONFIG)),

  assessResources: async (resourceIds: number[]): Promise<PatchInventoryResource[]> => {
    const raw = await send(() => api.post<{ results?: PatchInventoryResource[] }>(`${BASE}/resources/assess/`, { resource_ids: resourceIds }, REQUEST_CONFIG))
    return raw.results ?? []
  },

  /** Ask Azure to start an assessment and return at once. Azure takes minutes: read the result with `refreshAssessments`. */
  startAssessment: async (resourceIds: number[]): Promise<PatchInventoryResource[]> => {
    const raw = await send(() =>
      api.post<{ results?: PatchInventoryResource[] }>(`${BASE}/resources/assess/start/`, { resource_ids: resourceIds }, SLOW_REQUEST_CONFIG),
    )
    return raw.results ?? []
  },

  /** Read each machine's latest Azure assessment, including ones started in the Azure portal. */
  refreshAssessments: async (resourceIds: number[]): Promise<PatchInventoryResource[]> => {
    const raw = await send(() =>
      api.post<{ results?: PatchInventoryResource[] }>(`${BASE}/resources/assess/refresh/`, { resource_ids: resourceIds }, SLOW_REQUEST_CONFIG),
    )
    return raw.results ?? []
  },

  recommendResources: async (resourceIds: number[]) => {
    const raw = await send(() =>
      api.post<{ source: string; note: string; results: PatchRecommendationRow[] }>(`${BASE}/resources/recommend/`, { resource_ids: resourceIds }, REQUEST_CONFIG),
    )
    return raw
  },

  prevalidate: async (campaignId: string, env: PatchEnvironment): Promise<PrePatchCheck[]> => {
    const raw = await send(() => api.post<{ results?: PrePatchCheck[] }>(stagePath(campaignId, env, 'prevalidate'), undefined, REQUEST_CONFIG))
    return raw.results ?? []
  },

  /** Ask for a post-install assessment. The call returns at once; the page poll reads the finished checks. */
  postvalidate: async (campaignId: string, env: PatchEnvironment): Promise<PostPatchCheck[]> => {
    const raw = await send(() => api.post<{ results?: PostPatchCheck[] }>(stagePath(campaignId, env, 'postvalidate'), undefined, REQUEST_CONFIG))
    return normalizePostChecks(raw.results)
  },

  scheduleStage: (campaignId: string, env: PatchEnvironment, body: ScheduleStageBody) =>
    send(() => api.post<PatchScheduleRow>(stagePath(campaignId, env, 'schedule'), body, REQUEST_CONFIG)),

  cancelSchedule: (campaignId: string, env: PatchEnvironment, scheduleId: number) =>
    send(() => api.post<PatchScheduleRow>(`${stagePath(campaignId, env, 'schedule')}${scheduleId}/cancel/`, undefined, REQUEST_CONFIG)),

  getReport: (campaignId: string) => send(() => api.get<CampaignReport>(`${campaignPath(campaignId)}/report/`, REQUEST_CONFIG)),
}
